import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type {
  AdminCreditAdjustResult,
  AdminForceLogoutResult,
  AdminUserUpdateResult,
  ErrorAlert,
  UserRole,
  UserStatus,
  UserTier,
} from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';
import { adminUserDetailQueryKey } from './useAdminUserDetail.js';

export interface UseAdminUserMutationsResult {
  setRole: { mutate: (userId: string, role: UserRole, reason: string) => Promise<AdminUserUpdateResult>; isPending: boolean; error: ErrorAlert | null };
  setTier: { mutate: (userId: string, tier: UserTier, reason: string) => Promise<AdminUserUpdateResult>; isPending: boolean; error: ErrorAlert | null };
  setStatus: { mutate: (userId: string, status: UserStatus, reason: string) => Promise<AdminUserUpdateResult>; isPending: boolean; error: ErrorAlert | null };
  adjustCredits: { mutate: (userId: string, delta: number, reason: string) => Promise<AdminCreditAdjustResult>; isPending: boolean; error: ErrorAlert | null };
  forceLogout: { mutate: (userId: string, reason: string) => Promise<AdminForceLogoutResult>; isPending: boolean; error: ErrorAlert | null };
}

/**
 * The F54 sensitive-mutation set on a single user: role/tier/status
 * (independently, via the shared `admin.users.update` op — each call sends
 * only the ONE changed field + reason, matching the mockup's 3 separate
 * action cards even though the op accepts all three at once), credit
 * adjust, and force-logout. Every mutation invalidates this user's detail
 * query AND the `admin.users.list` table so a role/tier/status/credit
 * change is visible everywhere without a manual refresh.
 */
export function useAdminUserMutations(): UseAdminUserMutationsResult {
  const { wsClient } = useWs();
  const queryClient = useQueryClient();

  function invalidateFor(userId: string): void {
    void queryClient.invalidateQueries({ queryKey: adminUserDetailQueryKey(userId) });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'list'] });
  }

  const setRoleMutation = useMutation<AdminUserUpdateResult, ErrorAlert, { userId: string; role: UserRole; reason: string }>({
    mutationFn: ({ userId, role, reason }) => requestTyped(wsClient, 'admin.users.update', { userId, role, reason }),
    onSuccess: (_data, variables) => invalidateFor(variables.userId),
  });

  const setTierMutation = useMutation<AdminUserUpdateResult, ErrorAlert, { userId: string; tier: UserTier; reason: string }>({
    mutationFn: ({ userId, tier, reason }) => requestTyped(wsClient, 'admin.users.update', { userId, tier, reason }),
    onSuccess: (_data, variables) => invalidateFor(variables.userId),
  });

  const setStatusMutation = useMutation<AdminUserUpdateResult, ErrorAlert, { userId: string; status: UserStatus; reason: string }>({
    mutationFn: ({ userId, status, reason }) => requestTyped(wsClient, 'admin.users.update', { userId, status, reason }),
    onSuccess: (_data, variables) => invalidateFor(variables.userId),
  });

  const adjustCreditsMutation = useMutation<AdminCreditAdjustResult, ErrorAlert, { userId: string; delta: number; reason: string }>({
    mutationFn: ({ userId, delta, reason }) => requestTyped(wsClient, 'admin.users.creditAdjust', { userId, delta, reason }),
    onSuccess: (_data, variables) => invalidateFor(variables.userId),
  });

  const forceLogoutMutation = useMutation<AdminForceLogoutResult, ErrorAlert, { userId: string; reason: string }>({
    mutationFn: ({ userId, reason }) => requestTyped(wsClient, 'admin.users.forceLogout', { userId, reason }),
    onSuccess: (_data, variables) => invalidateFor(variables.userId),
  });

  return {
    setRole: {
      mutate: (userId, role, reason) => setRoleMutation.mutateAsync({ userId, role, reason }),
      isPending: setRoleMutation.isPending,
      error: setRoleMutation.error ?? null,
    },
    setTier: {
      mutate: (userId, tier, reason) => setTierMutation.mutateAsync({ userId, tier, reason }),
      isPending: setTierMutation.isPending,
      error: setTierMutation.error ?? null,
    },
    setStatus: {
      mutate: (userId, status, reason) => setStatusMutation.mutateAsync({ userId, status, reason }),
      isPending: setStatusMutation.isPending,
      error: setStatusMutation.error ?? null,
    },
    adjustCredits: {
      mutate: (userId, delta, reason) => adjustCreditsMutation.mutateAsync({ userId, delta, reason }),
      isPending: adjustCreditsMutation.isPending,
      error: adjustCreditsMutation.error ?? null,
    },
    forceLogout: {
      mutate: (userId, reason) => forceLogoutMutation.mutateAsync({ userId, reason }),
      isPending: forceLogoutMutation.isPending,
      error: forceLogoutMutation.error ?? null,
    },
  };
}
